import { Component, type ReactNode, type ErrorInfo } from "react";
import * as Sentry from "@sentry/react";
import { Error500 } from "@/components/errors/Error500";
import { PageSkeleton } from "@/components/PageSkeleton";
import { isChunkLoadError, isChunkReloadPending } from "@/lib/chunkReload";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Already reported by requestChunkReload, and the page is on its way out.
    if (isChunkReloadPending()) return;

    // Send to BetterStack error tracking via Sentry (single source of truth for React crashes)
    Sentry.captureException(error, {
      tags: {
        errorType: isChunkLoadError(error) ? "chunk_load" : "react_crash",
      },
      extra: {
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    });
  }

  handleRetry = () => {
    // React.lazy caches a rejected payload forever, so for a failed chunk load
    // clearing this state just re-renders straight back into the same throw.
    // Only a fresh document can recover.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // A recovery reload is already in flight. Showing the error screen for the
      // few hundred ms it takes to fetch a new document is worse than showing
      // nothing. PageSkeleton is safe here: this boundary sits outside every
      // provider, and PageSkeleton pulls in no context.
      if (isChunkReloadPending()) {
        return <PageSkeleton minimal />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <Error500 resetErrorBoundary={this.handleRetry} />;
    }

    return this.props.children;
  }
}
